import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useHasRole } from "@/hooks/useUserRoles";
import { Eye, Send, Undo2 } from "lucide-react";

export type ContentStatus = "draft" | "preview" | "published";

interface ContentStatusBadgeProps {
  status: ContentStatus | null | undefined;
  className?: string;
}

export function ContentStatusBadge({ status, className }: ContentStatusBadgeProps) {
  const value: ContentStatus = status ?? "draft";
  if (value === "published") {
    return (
      <Badge
        variant="outline"
        className={`bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800 ${className ?? ""}`}
      >
        Published
      </Badge>
    );
  }
  if (value === "preview") {
    return (
      <Badge
        variant="outline"
        className={`bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800 ${className ?? ""}`}
      >
        Preview
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className={className}>
      Draft
    </Badge>
  );
}

interface ContentStatusControlsProps {
  status: ContentStatus | null | undefined;
  onChange: (next: ContentStatus) => void;
  disabled?: boolean;
  size?: "sm" | "default";
}

/**
 * Renders status transition buttons (Send to Preview / Publish / Back to Draft)
 * Only rendered for users with admin or instructor role.
 */
export function ContentStatusControls({
  status,
  onChange,
  disabled,
  size = "sm",
}: ContentStatusControlsProps) {
  const isAdmin = useHasRole("admin");
  const isInstructor = useHasRole("instructor");
  if (!isAdmin && !isInstructor) return null;

  const value: ContentStatus = status ?? "draft";

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {value === "draft" && (
        <>
          <Button
            type="button"
            variant="outline"
            size={size}
            disabled={disabled}
            onClick={() => onChange("preview")}
          >
            <Eye className="h-3.5 w-3.5 mr-1" />
            Send to Preview
          </Button>
          <Button
            type="button"
            size={size}
            disabled={disabled}
            onClick={() => onChange("published")}
          >
            <Send className="h-3.5 w-3.5 mr-1" />
            Publish
          </Button>
        </>
      )}
      {value === "preview" && (
        <>
          <Button
            type="button"
            variant="outline"
            size={size}
            disabled={disabled}
            onClick={() => onChange("draft")}
          >
            <Undo2 className="h-3.5 w-3.5 mr-1" />
            Back to Draft
          </Button>
          <Button
            type="button"
            size={size}
            disabled={disabled}
            onClick={() => onChange("published")}
          >
            <Send className="h-3.5 w-3.5 mr-1" />
            Publish
          </Button>
        </>
      )}
      {value === "published" && (
        <Button
          type="button"
          variant="outline"
          size={size}
          disabled={disabled}
          onClick={() => onChange("draft")}
        >
          <Undo2 className="h-3.5 w-3.5 mr-1" />
          Back to Draft
        </Button>
      )}
    </div>
  );
}
